import React, { useState, useEffect, useCallback, Suspense } from 'react'
const Monitoring = React.lazy(() => import('./Monitoring.jsx'))
import './Monitoring.css'
import { useLang } from '../lib/useLang'

/**
 * MonitoringWrapper Component
 *
 * Simplified monitoring view:
 * - Shows latest monitoring result directly (no job list)
 * - If no monitoring exists → show "Trigger Monitoring" button
 * - If monitoring running → show progress
 * - If monitoring completed → show results with "Refresh" option
 */
export default function MonitoringWrapper({ workflowId, taskType = 'classification', initialView, onViewChange }) {
  const { t, lang } = useLang()
  const [latestJob, setLatestJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [triggering, setTriggering] = useState(false)
  const [error, setError] = useState('')

  // Load latest job for this workflow
  const loadLatestJob = useCallback(async () => {
    if (!workflowId) {
      setError(t('monitoringWrapper.noWorkflowId'))
      setLoading(false)
      return
    }

    try {
      const res = await fetch(`/api/monitoring/workflows/${workflowId}/latest`)

      if (res.status === 404) {
        // No monitoring job exists yet
        setLatestJob(null)
        setLoading(false)
        return
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.detail || t('monitoringWrapper.failedLoad'))
      }

      const job = await res.json()
      setLatestJob(job)
      setError('')
    } catch (e) {
      console.error('Failed to load latest job:', e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [workflowId])

  // Initial load
  useEffect(() => {
    loadLatestJob()
  }, [loadLatestJob])

  // Poll for running/pending jobs
  useEffect(() => {
    if (!latestJob || (latestJob.status !== 'running' && latestJob.status !== 'pending')) {
      return
    }

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/monitoring/jobs/${latestJob.job_id}/status`)
        if (!res.ok) return

        const statusData = await res.json()

        setLatestJob(prev => prev ? { ...prev, status: statusData.status, progress: statusData.progress } : null)

        // Reload full job data when completed
        if (statusData.status === 'completed' || statusData.status === 'failed') {
          loadLatestJob()
        }
      } catch (e) {
        console.error('Failed to poll job status:', e)
      }
    }, 2000) // Poll every 2 seconds

    return () => clearInterval(interval)
  }, [latestJob?.job_id, latestJob?.status, loadLatestJob])

  async function triggerMonitoring() {
    setTriggering(true)
    setError('')

    // Remember old job id to detect when new one appears
    const oldJobId = latestJob?.job_id

    try {
      const res = await fetch(`/api/monitoring/workflows/${workflowId}/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.detail || t('monitoringWrapper.failedTrigger'))
      }

      const triggerData = await res.json().catch(() => ({}))

      // Show pending state immediately for instant visual feedback
      if (triggerData.job_id) {
        setLatestJob({ job_id: triggerData.job_id, status: 'pending', progress: 0 })
      } else {
        setLatestJob({ job_id: '__pending__', status: 'pending', progress: 0 })
      }
      setTriggering(false)

      // Poll /latest to pick up real job data (progress, status updates)
      let attempts = 0
      const maxAttempts = 30
      const interval = setInterval(async () => {
        attempts++
        try {
          const latestRes = await fetch(`/api/monitoring/workflows/${workflowId}/latest`)
          if (latestRes.ok) {
            const job = await latestRes.json()
            if (job.job_id !== oldJobId || job.status === 'running' || job.status === 'pending') {
              clearInterval(interval)
              setLatestJob(job)
              return
            }
          }
        } catch {}
        if (attempts >= maxAttempts) {
          clearInterval(interval)
          loadLatestJob()
        }
      }, 2000)
    } catch (e) {
      setError(e.message)
      setTriggering(false)
      console.error('Failed to trigger monitoring:', e)
    }
  }

  function formatDate(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{
          background: 'rgba(14, 14, 16, 0.02)',
          border: '1px solid rgba(14, 14, 16, 0.04)',
          borderRadius: '16px',
          padding: '3rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '400px'
        }}>
          <div className="mon-spinner" />
        </div>
      </div>
    )
  }

  // No monitoring exists - show trigger card
  if (!latestJob) {
    return (
      <div style={{
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            borderRadius: '12px',
            color: '#F44336',
            marginBottom: '1.5rem',
            fontSize: '0.875rem',
            maxWidth: '480px',
            width: '100%'
          }}>
            {error}
          </div>
        )}

        <div
          onClick={!triggering ? triggerMonitoring : undefined}
          style={{
            background: 'rgba(14, 14, 16, 0.02)',
            border: '1px solid rgba(14, 14, 16, 0.04)',
            borderRadius: '20px',
            padding: '2.5rem',
            cursor: triggering ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            width: '100%',
            maxWidth: '480px',
            textAlign: 'center'
          }}
          onMouseEnter={e => {
            if (!triggering) {
              e.currentTarget.style.borderColor = 'rgba(255, 90, 31, 0.2)'
              e.currentTarget.style.background = 'rgba(14, 14, 16, 0.03)'
              e.currentTarget.style.transform = 'translateY(-2px)'
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.04)'
            e.currentTarget.style.background = 'rgba(14, 14, 16, 0.02)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(255, 90, 31, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#FF5A1F' }}>
              <path d="M3 3v18h18" />
              <path d="M18 9l-5 5-4-4-3 3" />
            </svg>
          </div>
          <h3 style={{
            margin: '0 0 0.75rem 0',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'rgba(14, 14, 16, 0.85)'
          }}>
            {t('monitoringWrapper.modelMonitoring')}
          </h3>
          <p style={{
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.6,
            color: 'rgba(14, 14, 16, 0.45)'
          }}>
            {t('monitoringWrapper.description')}
          </p>
          <span style={{
            fontSize: '1rem',
            fontWeight: 500,
            color: '#FF5A1F',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {triggering ? (
              <>
                <span className="mon-spinner-small" style={{ width: '16px', height: '16px' }} />
                {t('monitoringWrapper.starting')}
              </>
            ) : (
              t('monitoringWrapper.runMonitoring')
            )}
          </span>
        </div>
      </div>
    )
  }

  // Job is running/pending - show progress
  if (latestJob.status === 'running' || latestJob.status === 'pending') {
    const progress = latestJob.progress || 0

    return (
      <div style={{
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          background: 'rgba(14, 14, 16, 0.02)',
          border: '1px solid rgba(255, 90, 31, 0.15)',
          borderRadius: '20px',
          padding: '2.5rem',
          width: '100%',
          maxWidth: '480px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(255, 90, 31, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            <div className="mon-spinner" style={{ width: '32px', height: '32px' }} />
          </div>
          <h3 style={{
            margin: '0 0 0.75rem 0',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'rgba(14, 14, 16, 0.85)'
          }}>
            {latestJob.status === 'pending' ? t('monitoringWrapper.startingMonitoring') : t('monitoringWrapper.analyzingData')}
          </h3>
          <p style={{
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            color: 'rgba(14, 14, 16, 0.45)'
          }}>
            {latestJob.status === 'pending'
              ? t('monitoringWrapper.preparingData')
              : t('monitoringWrapper.processingProgress', { percent: Math.round(progress * 100) })
            }
          </p>

          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(14, 14, 16, 0.09)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${Math.max(Math.min(progress * 100, 100), 5)}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #FF5A1F, #C43D10)',
              transition: 'width 0.3s ease',
              borderRadius: '3px'
            }} />
          </div>
        </div>
      </div>
    )
  }

  // Job failed - show error with retry option
  if (latestJob.status === 'failed') {
    return (
      <div style={{ padding: '2rem' }}>
        <div
          onClick={!triggering ? triggerMonitoring : undefined}
          style={{
            background: 'rgba(244, 67, 54, 0.05)',
            border: '1px solid rgba(244, 67, 54, 0.15)',
            borderRadius: '16px',
            padding: '1.5rem',
            cursor: triggering ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            maxWidth: '500px'
          }}
          onMouseEnter={e => {
            if (!triggering) {
              e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.3)'
              e.currentTarget.style.transform = 'translateY(-2px)'
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.15)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'rgba(244, 67, 54, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#F87171' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#F87171'
          }}>
            {t('monitoringWrapper.monitoringFailed')}
          </h3>
          <p style={{
            margin: '0 0 0.5rem 0',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'rgba(14, 14, 16, 0.45)'
          }}>
            {latestJob.error_message || t('monitoringWrapper.errorOccurred')}
          </p>
          <p style={{
            margin: '0 0 1.5rem 0',
            fontSize: '0.75rem',
            color: 'rgba(14, 14, 16, 0.27)'
          }}>
            {t('monitoringWrapper.lastAttempt', { date: formatDate(latestJob.created_at) })}
          </p>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#FF5A1F',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            {triggering ? (
              <>
                <span className="mon-spinner-small" style={{ width: '14px', height: '14px' }} />
                {t('monitoringWrapper.starting')}
              </>
            ) : (
              t('monitoringWrapper.retryMonitoring')
            )}
          </span>
        </div>
      </div>
    )
  }

  // Job completed - show results with Monitoring component
  // Pass onRefresh callback to allow refreshing from within Monitoring component
  return (
    <Suspense fallback={<div style={{ padding: '2rem' }}><div className="mon-spinner" /></div>}>
      <Monitoring
        jobId={latestJob.job_id}
        taskType={latestJob.task_type || taskType}
        lastUpdated={formatDate(latestJob.updated_at || latestJob.created_at)}
        onRefresh={triggerMonitoring}
        refreshing={triggering}
        initialView={initialView}
        onViewChange={onViewChange}
      />
    </Suspense>
  )
}
